import React from 'react';
import type { FormInstance } from 'antd';
import { Button, Col, DatePicker, Form, Input, Row, Select } from 'antd';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import styles from '../../pages/UpdatePersonalProfilePage.module.css';

const { Option } = Select;

const TRAINING_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'official', label: 'Chính quy' },
    { value: 'informal', label: 'Không chính quy'},
    { value: 'parttime-learning', label: 'Vừa làm vừa học' },
    { value: 'continuing', label: 'Liên thông'},
    { value: 'distance', label: 'Đào tạo từ xa'},
    { value: 'vocational', label: 'Đào tạo nghề'},
    { value: 'international', label: 'Đào tạo quốc tế'}
];

const EDUCATION_LEVEL_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'secondary', label: 'Trung cấp' },
  { value: 'college', label: 'Cao đẳng' },
  { value: 'university', label: 'Đại học' },
  { value: 'graduate', label: 'Sau đại học' },
  { value: 'other', label: 'Khác' },
];

interface UpdateEducationSectionProps {
  /**
   * userId của nhân sự cần hiển thị học vấn.
   * - Nếu truyền vào: ưu tiên dùng userId này (dùng cho màn Quản lý nhân sự chỉnh hồ sơ staff).
   * - Nếu không truyền: fallback về user hiện tại từ AuthContext (dùng cho trang hồ sơ cá nhân tự cập nhật).
   */
  userId?: number;
  form: FormInstance;
}

export const UpdateEducationSection: React.FC<UpdateEducationSectionProps> = () => {
  return (
    <div className={styles.section}>
      <Form.List name="educationHistories">
        {(fields, { add, remove }) => (
          <div className={styles.familySection}>
            {fields.length === 0 && (
              <div className={styles.familyEmptyState}>
                <div style={{ marginBottom: 16 }}>
                  Chưa có học vấn nào. Nhấn &quot;Thêm học vấn&quot; để bắt đầu.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    icon={<Plus size={16} />}
                    size="middle"
                    className={styles.familyAddButton}
                    onClick={() => add({})}
                  >
                    Thêm học vấn
                  </Button>
                </div>
              </div>
            )}

            {fields.map((field, index) => (
              <div key={field.key} className={styles.workCard}>
                <div className={styles.workCardHeader}>
                  <span className={styles.workCardSubtitle}>Học vấn {index + 1}</span>
                  <Button
                    type="text"
                    danger
                    icon={<Trash2 size={16} />}
                    className={styles.workCardRemoveButton}
                    onClick={() => remove(field.name)}
                  >
                    Xóa
                  </Button>
                </div>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'fromDate']} label="Từ ngày">
                      <DatePicker
                        placeholder="Chọn ngày"
                        size="large"
                        format="DD/MM/YYYY"
                        className={styles.fullWidth}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'toDate']} label="Đến ngày">
                      <DatePicker
                        placeholder="Chọn ngày"
                        size="large"
                        format="DD/MM/YYYY"
                        className={styles.fullWidth}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'trainingType']} label="Hình thức đào tạo">
                      <Select
                        placeholder="Chọn hình thức"
                        size="large"
                        className={styles.select}
                        suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                        allowClear
                      >
                        {TRAINING_TYPE_OPTIONS.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'major']} label="Chuyên ngành">
                      <Input placeholder="Chuyên ngành" size="large" className={styles.input} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'educationLevel']} label="Trình độ học vấn">
                      <Select
                        placeholder="Chọn trình độ"
                        size="large"
                        className={styles.select}
                        suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                        allowClear
                      >
                        {EDUCATION_LEVEL_OPTIONS.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={[field.name, 'school']} label="Nơi đào tạo">
                      <Input placeholder="Tên trường/ Cơ sở đào tạo" size="large" className={styles.input} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            ))}

            {/* Nút Thêm học vấn ở cuối danh sách */}
            {fields.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<Plus size={16} />}
                  size="middle"
                  className={styles.familyAddButton}
                  onClick={() => add({})}
                >
                  Thêm học vấn
                </Button>
              </div>
            )}
          </div>
        )}
      </Form.List>
    </div>
  );
};
